// src/components/notify/NotificationItem.js
// One row of the notification centre: priority accent, category icon, ack call-to-action,
// escalation badge, AI source sparkle, and a read / snooze / archive / delete menu.
import React, { memo, useCallback } from 'react';
import {
    Badge, Box, Button, Flex, HStack, Icon, IconButton, Menu, MenuButton, MenuDivider, MenuItem, MenuList,
    Text, Tooltip, useColorModeValue,
} from '@chakra-ui/react';
import {
    MdArchive, MdAutoAwesome, MdCheckCircle, MdDeleteOutline, MdDoneAll, MdMarkEmailUnread, MdMoreVert,
    MdOpenInNew, MdSnooze, MdTrendingUp, MdUnarchive,
} from 'react-icons/md';
import { categoryMeta, priorityMeta, sourceMeta, timeAgo, fmtDateTime } from './notifyFormat';

const SNOOZE = [
    { label: '15 minutes', minutes: 15 },
    { label: '1 hour', minutes: 60 },
    { label: '4 hours', minutes: 240 },
    { label: 'Tomorrow', minutes: 24 * 60 },
];

function NotificationItem({ n, onOpen, onRead, onUnread, onAck, onArchive, onUnarchive, onSnooze, onDelete, compact = false }) {
    const bg = useColorModeValue('white', 'gray.800');
    const unreadBg = useColorModeValue('purple.50', 'whiteAlpha.100');
    const hoverBg = useColorModeValue('gray.50', 'whiteAlpha.50');
    const border = useColorModeValue('gray.100', 'gray.700');
    const muted = useColorModeValue('gray.500', 'gray.400');
    const bodyColor = useColorModeValue('gray.600', 'gray.300');
    const iconBgOpacity = useColorModeValue('50', '900');

    const cat = categoryMeta(n.category);
    const pr = priorityMeta(n.priority);
    const src = sourceMeta(n.source);
    const needsAck = n.requires_ack && !n.acknowledged_at;
    const accent = n.priority === 'critical' ? 'red.500' : n.priority === 'high' ? 'orange.400' : !n.is_read ? 'purple.400' : 'transparent';

    const open = useCallback(() => onOpen?.(n), [onOpen, n]);
    const onKey = useCallback((e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); } }, [open]);
    const stop = (fn) => (e) => { e.stopPropagation(); fn?.(); };

    return (
        <Box
            role="article"
            aria-label={`${n.is_read ? '' : 'Unread. '}${pr.label} priority. ${n.title}`}
            bg={n.is_read ? bg : unreadBg}
            _hover={{ bg: n.is_read ? hoverBg : unreadBg }}
            borderBottomWidth="1px"
            borderColor={border}
            borderLeftWidth="4px"
            borderLeftColor={accent}
            px={3}
            py={compact ? 2 : 3}
            cursor="pointer"
            tabIndex={0}
            onClick={open}
            onKeyDown={onKey}
            transition="background 0.15s"
        >
            <Flex gap={3} align="flex-start">
                <Flex flexShrink={0} mt={0.5} bg={`${cat.color}.${iconBgOpacity}`} borderRadius="lg" p={2}>
                    <Icon as={cat.icon} color={`${cat.color}.500`} boxSize={4} />
                </Flex>
                <Box flex="1" minW={0}>
                    <Flex justify="space-between" align="flex-start" gap={2}>
                        <Text fontSize="sm" fontWeight={n.is_read ? '600' : '800'} noOfLines={2} wordBreak="break-word">{n.title}</Text>
                        <Tooltip label={fmtDateTime(n.created_at)} hasArrow openDelay={400}>
                            <Text fontSize="xs" color={muted} flexShrink={0} whiteSpace="nowrap">{timeAgo(n.created_at)}</Text>
                        </Tooltip>
                    </Flex>
                    {n.body && n.body !== n.title && (
                        <Text fontSize="sm" color={bodyColor} noOfLines={compact ? 2 : 3} mt={0.5} whiteSpace="pre-line">{n.body}</Text>
                    )}
                    <HStack spacing={1.5} mt={1.5} flexWrap="wrap">
                        {n.priority !== 'normal' && <Badge colorScheme={pr.color} variant={n.priority === 'critical' ? 'solid' : 'subtle'} fontSize="2xs">{pr.label}</Badge>}
                        <Badge colorScheme={cat.color} variant="subtle" fontSize="2xs">{cat.label}</Badge>
                        {src.ai && (
                            <Badge colorScheme="purple" variant="outline" fontSize="2xs">
                                <HStack spacing={0.5}><Icon as={MdAutoAwesome} boxSize={2.5} /><Text as="span">{src.label}</Text></HStack>
                            </Badge>
                        )}
                        {n.escalation_level > 0 && !n.acknowledged_at && (
                            <Tooltip label="Not acknowledged in time — your supervisor was notified" hasArrow>
                                <Badge colorScheme="red" variant="outline" fontSize="2xs"><HStack spacing={0.5}><Icon as={MdTrendingUp} boxSize={2.5} /><Text as="span">Escalated L{n.escalation_level}</Text></HStack></Badge>
                            </Tooltip>
                        )}
                        {n.acknowledged_at && <Badge colorScheme="green" variant="subtle" fontSize="2xs">Acknowledged</Badge>}
                        {n.entity?.type === 'work_order' && <Text fontSize="2xs" color={muted}>WO #{n.entity.id}</Text>}
                    </HStack>
                    {needsAck && onAck && (
                        <Button mt={2} size="xs" colorScheme={n.priority === 'critical' ? 'red' : 'purple'} leftIcon={<MdCheckCircle />} onClick={stop(() => onAck(n))}>
                            Acknowledge
                        </Button>
                    )}
                </Box>
                <Menu isLazy placement="bottom-end">
                    <MenuButton as={IconButton} icon={<MdMoreVert />} size="xs" variant="ghost" aria-label="Notification actions" onClick={(e) => e.stopPropagation()} />
                    <MenuList fontSize="sm" onClick={(e) => e.stopPropagation()} zIndex="popover">
                        {(n.action_url || n.entity) && <MenuItem icon={<MdOpenInNew />} onClick={open}>Open</MenuItem>}
                        {n.is_read
                            ? <MenuItem icon={<MdMarkEmailUnread />} onClick={() => onUnread?.(n)}>Mark as unread</MenuItem>
                            : <MenuItem icon={<MdDoneAll />} onClick={() => onRead?.(n)}>Mark as read</MenuItem>}
                        {!n.archived_at && SNOOZE.map(s => (
                            <MenuItem key={s.minutes} icon={<MdSnooze />} onClick={() => onSnooze?.(n, s.minutes)}>Snooze {s.label.toLowerCase()}</MenuItem>
                        ))}
                        <MenuDivider />
                        {n.archived_at
                            ? <MenuItem icon={<MdUnarchive />} onClick={() => onUnarchive?.(n)}>Move to inbox</MenuItem>
                            : <MenuItem icon={<MdArchive />} onClick={() => onArchive?.(n)}>Archive</MenuItem>}
                        <MenuItem icon={<MdDeleteOutline />} color="red.500" onClick={() => onDelete?.(n)}>Delete</MenuItem>
                    </MenuList>
                </Menu>
            </Flex>
        </Box>
    );
}

export default memo(NotificationItem);
